import api from "services/api";
import serveRequestErrors from "common/serve-request-errors";

import { Menu, MenuItem } from "@mui/material";
import { formatDate } from "common/calendar";
import AppTable, { AppTableCell, AppTableMoreIcon } from "components/app-table";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import downloadIcon from "assets/images/download-black-icon.svg";
import sanitizeObject from "common/sanitize-object";
import COMMON from "common";
import AppEmptyState from "components/app-empty-state";

const AppEmployeeDocumentTab = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const memoSetSearchParams = useRef(setSearchParams);
	const paramsRef = useRef({
		page: parseInt(searchParams.get("page") || 1),
		sort: searchParams.get("sort") || "",
		tab: "Document",
	});
	const [anchorEl, setAnchorEl] = useState(null);
	const selectedRowRef = useRef(null);
	const [data, setData] = useState({ page: 0, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });
	const isEmptyState = useMemo(() => COMMON.TABLE_REQUEST_STATUS.INSTANCE === data.status, [data.status]);

	const onHandleGetList = useCallback(async () => {
		let response = null;

		setData({ page: paramsRef.current.page, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });

		try {
			const payload = { ...paramsRef.current, size: 10 };

			memoSetSearchParams.current(sanitizeObject(payload), { replace: true });

			payload.page = paramsRef.current.page - 1;

			response = await api.get.employee.employeeDocument(payload);
		} catch (error) {
			serveRequestErrors(error);
		}

		if (response) {
			setData((prev) => ({
				...prev,
				page: paramsRef.current.page,
				prev: !response.first,
				next: !response.last,
				items: response.content,
				total: response.totalPages,
				totalElements: response.totalElements,
				offset: response?.pageable?.offset || 0,
				status: response?.pageable,
			}));
		}
	}, []);

	const onHandleDownload = useCallback(() => {
		const file = selectedRowRef.current;
		if (!file) return;

		window.open(file.filePath, "_self");
		setAnchorEl(null);
	}, []);

	const onHandlePagination = (event) => {
		const control = event.currentTarget?.getAttribute("data-ctrl");

		if (control) {
			if (control === "prev") {
				if (paramsRef.current.page <= 1) return;
				paramsRef.current.page -= 1;
			} else {
				if (paramsRef.current.page >= data.total) return;
				paramsRef.current.page += 1;
			}
		} else {
			paramsRef.current.page = event.target.value;
		}

		onHandleGetList();
	};

	const onHandleSort = (id, order) => {
		paramsRef.current.sort = order ? id + "," + order : "";

		paramsRef.current.page = 1;

		onHandleGetList();
	};

	const onHandleTableActionMenu = (event, row) => {
		selectedRowRef.current = row.original;
		setAnchorEl(event.currentTarget);
	};

	const onHandleCloseActionMenu = () => {
		setAnchorEl(null);
	};

	const CreatedDateCell = useCallback(({ row }) => <AppTableCell left value={formatDate(row.original.createdDate)} />, []);

	const MenuViewCell = useCallback(({ row }) => {
		return <AppTableMoreIcon onClick={(event) => onHandleTableActionMenu(event, row)} />;
	}, []);

	const columns = useMemo(
		() => [
			{
				Header: "File Name",
				accessor: "fileName",
				disableSortBy: false,
			},
			{
				Header: "Uploaded By",
				accessor: "uploadedBy",
				disableSortBy: false,
			},
			{
				Header: "Uploaded Date",
				accessor: "createdDate",
				disableSortBy: false,
				Cell: CreatedDateCell,
			},
			{
				Header: "",
				accessor: "*",
				disableSortBy: true,
				Cell: MenuViewCell,
			},
		],
		[CreatedDateCell, MenuViewCell]
	);

	useEffect(() => {
		onHandleGetList();
	}, [onHandleGetList]);

	return (
		<>
			{isEmptyState && <AppEmptyState title="No records found" description="You don’t have any records yet" disabledButton />}
			{!isEmptyState && (
				<Fragment>
					<AppTable columns={columns} pages={data} onHandlePagination={onHandlePagination} onHandleSort={onHandleSort} />

					<Menu
						classes={{ root: "app-table-menu" }}
						anchorEl={anchorEl}
						open={!!anchorEl}
						onClose={onHandleCloseActionMenu}
						anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
						transformOrigin={{ vertical: "top", horizontal: "right" }}
					>
						<MenuItem onClick={onHandleDownload}>
							<img className="app-table-menu__icon" src={downloadIcon} alt="Download" />
							Download
						</MenuItem>
					</Menu>
				</Fragment>
			)}
		</>
	);
};

export default AppEmployeeDocumentTab;
